import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Department = { id: number; code: string; name: string; is_active: boolean }

type JobProfile = {
  id: number
  code: string
  name: string
  description: string
  default_department: number | null
  default_department_name: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

type CreateInput = {
  name: string
  description: string
  default_department: number | null
  is_active: boolean
}

const FILTERS = ['ALL', 'ACTIVE', 'INACTIVE'] as const
type Filter = typeof FILTERS[number]

export default function HRJobProfiles() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const profilesQ = useQuery({
    queryKey: ['hr', 'job-profiles'],
    queryFn: async () => (await api.get<JobProfile[]>('/hr/job-profiles/')).data,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filter, setFilter] = useState<Filter>('ACTIVE')
  const [search, setSearch] = useState('')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<JobProfile>('/hr/job-profiles/', draft)).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['hr', 'job-profiles'] })
      toast.success('Job profile created')
      setCreating(false)
      navigate(`/admin/hr/job-profiles/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/job-profiles/${id}/`),
    onSuccess: () => {
      toast.success('Job profile deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'job-profiles'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const profiles = profilesQ.data || []
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)

  const filtered = profiles.filter((p) => {
    if (filter === 'ACTIVE' && !p.is_active) return false
    if (filter === 'INACTIVE' && p.is_active) return false
    if (search.trim()) {
      const needle = search.trim().toLowerCase()
      const hay = `${p.code} ${p.name} ${p.description}`.toLowerCase()
      if (!hay.includes(needle)) return false
    }
    return true
  })

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Job Profiles</h2>
          <p className="text-sm text-muted-foreground">
            {profiles.length} total · {profiles.filter((p) => p.is_active).length} active
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New job profile</Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded ${filter === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
        <div className="ml-auto w-64">
          <Input
            placeholder="Search by code, name…"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
          />
        </div>
      </div>

      {profilesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          {profiles.length === 0
            ? 'No job profiles yet. Create one to start defining the roles your org uses.'
            : 'No job profiles match this filter.'}
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Default department</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/hr/job-profiles/${p.id}`)}
                >
                  <Td className="font-mono text-xs">{p.code}</Td>
                  <Td>
                    <div className="font-medium text-foreground">{p.name}</div>
                    {p.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>
                    )}
                  </Td>
                  <Td className="text-sm">
                    {p.default_department_name || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td>
                    {p.is_active ? (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-gray-100 text-gray-700">
                        Inactive
                      </span>
                    )}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/hr/job-profiles/${p.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(`job profile ${p.code} — ${p.name}`, () => deleteMut.mutate(p.id))}
                          className="text-destructive"
                        >
                          Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New job profile"
        description="Define a reusable role. Specific people are matched to it through their Employment."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="jp-name">Name *</FieldLabel>
            <Input id="jp-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })}
              placeholder="Senior Software Engineer" />
          </Field>

          <Field>
            <FieldLabel htmlFor="jp-desc">Description</FieldLabel>
            <Textarea id="jp-desc" rows={3} value={draft.description}
              onChange={(ev) => setDraft({ ...draft, description: ev.target.value })}
              placeholder="Responsibilities, required skills…" />
          </Field>

          <Field>
            <FieldLabel htmlFor="jp-dept">Default department</FieldLabel>
            <SearchableSelect
              id="jp-dept"
              value={draft.default_department ? String(draft.default_department) : NONE}
              onChange={(v) => setDraft({ ...draft, default_department: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...departments.map((d) => ({
                  value: String(d.id), label: d.name, hint: d.code,
                })),
              ]}
            />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return { name: '', description: '', default_department: null, is_active: true }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
